/**
 * Per-app secrets vault — list + add/rotate + delete. Owner/admin only
 * for write actions; viewing names is allowed for anyone with access to
 * the app. Values never round-trip the server in plaintext after they
 * arrive, and the API has no read-back endpoint — the only path that
 * decrypts is the per-app Worker via env.SECRETS.<NAME> at runtime.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { AppTabs } from '../components/AppTabs.js';
import { ManagerOnlyPage } from '../components/ManagerOnly.js';
import { IconKey, IconLock, IconShield, IconTrash } from '../components/Icons.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Mono,
  PageHeader,
  PageSkeleton,
  inputClass,
} from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { useAppPageWith } from '../lib/useAppPage.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';

interface SecretSummary {
  id: string;
  name: string;
  last_rotated_at: number;
  created_at: number;
}

interface MtlsCertSummary {
  id: string;
  binding_name: string;
  issuer: string | null;
  serial_number: string | null;
  expires_at: number | null;
  created_at: number;
}

const THIRTY_DAYS_S = 30 * 24 * 60 * 60;

function expiryLabel(expiresAt: number | null): { text: string; className: string } {
  if (expiresAt === null) return { text: 'expiry unknown', className: 'text-muted' };
  const date = new Date(expiresAt * 1000).toISOString().slice(0, 10);
  const secondsLeft = expiresAt - Math.floor(Date.now() / 1000);
  if (secondsLeft <= 0) return { text: `expired ${date}`, className: 'text-danger font-medium' };
  if (secondsLeft <= THIRTY_DAYS_S)
    return { text: `expires ${date}`, className: 'text-warn font-medium' };
  return { text: `expires ${date}`, className: 'text-muted' };
}

export function Secrets() {
  const { slug } = useParams();
  const { app, payload, loading, error, reload, setPayload } = useAppPageWith(slug, async (s) => {
    const [secretsRes, certsRes] = await Promise.all([
      api.get<{ secrets: SecretSummary[] }>(`/api/apps/${encodeURIComponent(s)}/secrets`),
      api.get<{ certificates: MtlsCertSummary[] }>(
        `/api/apps/${encodeURIComponent(s)}/mtls-certificates`,
      ),
    ]);
    return { secrets: secretsRes.secrets, certificates: certsRes.certificates };
  });
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [certName, setCertName] = useState('');
  const [certPem, setCertPem] = useState('');

  if (loading) return <PageSkeleton rows={4} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app || !payload) return null;
  const secrets = payload.secrets;
  const certificates = payload.certificates;

  if (!app.is_manager) {
    return (
      <ManagerOnlyPage
        app={app}
        title="Secrets"
        description="API keys and credentials this app reads at runtime."
      />
    );
  }

  async function setSecret(e: React.FormEvent) {
    e.preventDefault();
    if (!slug || !name || !value) return;
    await runAction({
      do: () =>
        api.post<{ name: string; created: boolean }>(
          `/api/apps/${encodeURIComponent(slug)}/secrets`,
          { name, value },
        ),
      onSuccess: (res) => {
        setName('');
        setValue('');
        return res.created ? `Created ${res.name}.` : `Rotated ${res.name}.`;
      },
      errorPrefix: "Couldn't save",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function deleteSecret(secretName: string) {
    if (!slug) return;
    if (
      !confirm(
        `Delete secret "${secretName}"? The app will throw on env.SECRETS.${secretName} until you add it back.`,
      )
    )
      return;
    await runAction({
      do: () =>
        api.delete(
          `/api/apps/${encodeURIComponent(slug)}/secrets/${encodeURIComponent(secretName)}`,
        ),
      onSuccess: () => `Deleted ${secretName}.`,
      errorPrefix: "Couldn't delete",
      setBusy,
      setMessage: setActionMessage,
    });
    // Optimistic local removal — server already confirmed.
    setPayload((prev) => ({ ...prev, secrets: prev.secrets.filter((x) => x.name !== secretName) }));
  }

  async function uploadCert(e: React.FormEvent) {
    e.preventDefault();
    if (!slug || !certName || !certPem) return;
    await runAction({
      do: () =>
        api.post<{ binding_name: string; created: boolean; rebound_envs: string[] }>(
          `/api/apps/${encodeURIComponent(slug)}/mtls-certificates`,
          { binding_name: certName, pem: certPem },
        ),
      onSuccess: (res) => {
        setCertName('');
        setCertPem('');
        const live =
          res.rebound_envs.length > 0
            ? ` Deployed Workers rebound (${res.rebound_envs.join(', ')}) — it's live now.`
            : ' It attaches on the next deploy.';
        return `${res.created ? 'Uploaded' : 'Rotated'} ${res.binding_name}.${live}`;
      },
      errorPrefix: "Couldn't upload",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function deleteCert(bindingName: string) {
    if (!slug) return;
    if (
      !confirm(
        `Remove certificate "${bindingName}"? Calls through env.${bindingName}.fetch(...) will fail immediately, and the certificate is deleted from Cloudflare — re-adding it needs the PEM again.`,
      )
    )
      return;
    await runAction({
      do: () =>
        api.delete(
          `/api/apps/${encodeURIComponent(slug)}/mtls-certificates/${encodeURIComponent(bindingName)}`,
        ),
      onSuccess: () => `Removed ${bindingName}.`,
      errorPrefix: "Couldn't remove",
      setBusy,
      setMessage: setActionMessage,
    });
    setPayload((prev) => ({
      ...prev,
      certificates: prev.certificates.filter((x) => x.binding_name !== bindingName),
    }));
  }

  function readPemFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCertPem(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsText(file);
    // Allow re-picking the same file after an error.
    e.target.value = '';
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title="Secrets"
        description={
          <>
            API keys and credentials your app reads at runtime via{' '}
            <Mono>env.SECRETS.&lt;NAME&gt;</Mono>. Encrypted at rest, never read back from the
            portal.
          </>
        }
      />

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0 space-y-5">
          <Banner tone="info">
            <span className="inline-flex items-center gap-1.5">
              <IconShield size={14} />
              Values are write-only. Your AI agent only sees the names, never the values.
            </span>
          </Banner>

          {actionMessage && (
            <Banner tone={actionMessage.tone} onDismiss={() => setActionMessage(null)}>
              {actionMessage.text}
            </Banner>
          )}

          <Card>
            <CardHeader title="Add or rotate" description="Pick a name and a new value." />
            <form
              onSubmit={(e) => void setSecret(e)}
              className="px-5 py-4 grid sm:grid-cols-[200px_1fr_auto] gap-3 items-end"
            >
              <Field label="Name" required>
                <input
                  type="text"
                  required
                  pattern="[A-Z][A-Z0-9_]{0,63}"
                  value={name}
                  onChange={(e) => setName(e.target.value.toUpperCase())}
                  placeholder="OPENAI_API_KEY"
                  className={`${inputClass} font-mono`}
                />
              </Field>
              <Field label="Value" required>
                <input
                  type="password"
                  required
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="sk-…"
                  autoComplete="off"
                  className={`${inputClass} font-mono`}
                />
              </Field>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={busy}
                disabled={busy || !name || !value}
              >
                Save
              </Button>
            </form>
          </Card>

          {secrets.length === 0 ? (
            <EmptyState
              icon={<IconKey size={18} />}
              title="No secrets yet"
              description={
                <>
                  Once added, your app reads them as <Mono>await env.SECRETS.NAME</Mono>.
                </>
              }
            />
          ) : (
            <Card>
              <CardHeader
                title="Stored secrets"
                description={`${secrets.length} secret${secrets.length === 1 ? '' : 's'}`}
              />
              <ul className="divide-y divide-line">
                {secrets.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
                  >
                    <span className="min-w-0 inline-flex items-center gap-3">
                      <IconKey size={14} className="text-faint shrink-0" />
                      <Mono className="font-medium">{s.name}</Mono>
                      <span className="text-xs text-muted">
                        rotated {relTime(s.last_rotated_at)}
                      </span>
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void deleteSecret(s.name)}
                      disabled={busy}
                      className="!text-danger hover:!bg-danger-soft"
                    >
                      <IconTrash size={13} />
                      Delete
                    </Button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card>
            <CardHeader
              title="mTLS client certificates"
              description={
                <>
                  For APIs that require mutual TLS. Your app calls{' '}
                  <Mono>env.&lt;NAME&gt;.fetch(url)</Mono> and the Worker presents the certificate
                  during the TLS handshake. The PEM (certificate + unencrypted private key) goes
                  straight to Cloudflare — it can never be read back.
                </>
              }
            />
            <form onSubmit={(e) => void uploadCert(e)} className="px-5 py-4 space-y-3">
              <div className="grid sm:grid-cols-[200px_1fr] gap-3">
                <Field label="Binding name" required>
                  <input
                    type="text"
                    required
                    pattern="[A-Z][A-Z0-9_]{0,63}"
                    value={certName}
                    onChange={(e) => setCertName(e.target.value.toUpperCase())}
                    placeholder="MCMASTER_CERT"
                    className={`${inputClass} font-mono`}
                  />
                </Field>
                <Field label="PEM (certificate + private key)" required>
                  <textarea
                    required
                    value={certPem}
                    onChange={(e) => setCertPem(e.target.value)}
                    placeholder={'-----BEGIN CERTIFICATE-----\n…\n-----BEGIN PRIVATE KEY-----\n…'}
                    rows={4}
                    spellCheck={false}
                    className={`${inputClass} font-mono text-xs`}
                  />
                </Field>
              </div>
              <div className="flex items-center justify-between gap-3">
                <label className="text-xs text-muted cursor-pointer hover:text-body">
                  …or choose a .pem file
                  <input
                    type="file"
                    accept=".pem,.crt,.key,.txt"
                    onChange={readPemFile}
                    className="hidden"
                  />
                </label>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  loading={busy}
                  disabled={busy || !certName || !certPem}
                >
                  Upload
                </Button>
              </div>
            </form>
            {certificates.length > 0 && (
              <ul className="divide-y divide-line border-t border-line">
                {certificates.map((c) => {
                  const expiry = expiryLabel(c.expires_at);
                  return (
                    <li
                      key={c.id}
                      className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
                    >
                      <span className="min-w-0 inline-flex items-center gap-3">
                        <IconLock size={14} className="text-faint shrink-0" />
                        <Mono className="font-medium">{c.binding_name}</Mono>
                        {c.issuer && (
                          <span className="text-xs text-muted truncate" title={c.issuer}>
                            {c.issuer}
                          </span>
                        )}
                        <span className={`text-xs shrink-0 ${expiry.className}`}>
                          {expiry.text}
                        </span>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void deleteCert(c.binding_name)}
                        disabled={busy}
                        className="!text-danger hover:!bg-danger-soft"
                      >
                        <IconTrash size={13} />
                        Remove
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </main>
      </div>
    </div>
  );
}
